import React, { useEffect, useRef, useState } from 'react';
import { adImageUrl, api, ApiError } from './api';
import { longDate } from './dates';
import { Modal } from './Modal';
import { AdInfo } from './types';

/** Space above page 1 for the day's two advertisement pictures (left and right). Shows nothing when there are none. */
export function AdBanner({ date, version }: { date: string; version: number }) {
  const [ads, setAds] = useState<AdInfo[]>([]);
  useEffect(() => {
    let live = true;
    setAds([]);
    api
      .ads(date)
      .then((a) => live && setAds(a))
      .catch(() => live && setAds([]));
    return () => {
      live = false;
    };
  }, [date, version]);

  if (!ads.length) return null;
  return (
    <aside className="pm-slot pm-two" aria-label="Sponsors">
      <span className="pm-tag">Advertisement</span>
      {ads.map((a) => (
        <img key={a.id} src={adImageUrl(a)} alt="Sponsor" />
      ))}
    </aside>
  );
}

const SLOTS = [
  { slot: 1, label: 'Left picture' },
  { slot: 2, label: 'Right picture' },
];

/** Ask for the day's advertisement pictures. One is enough (left or right); each saved picture takes the place of that side's default video. */
export function AdDialog({ date, onClose, onChanged }: { date: string; onClose: () => void; onChanged: () => void }) {
  const [current, setCurrent] = useState<AdInfo[] | undefined>(undefined);
  const [files, setFiles] = useState<Record<number, File | undefined>>({});
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputs = useRef<Record<number, HTMLInputElement | null>>({});

  useEffect(() => {
    api
      .ads(date)
      .then(setCurrent)
      .catch(() => setCurrent([]));
  }, [date]);

  useEffect(() => {
    const urls: Record<number, string> = {};
    for (const s of SLOTS) {
      const f = files[s.slot];
      if (f) urls[s.slot] = URL.createObjectURL(f);
    }
    setPreviews(urls);
    return () => Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const pick = (slot: number, f: File | undefined | null) => {
    setErr('');
    if (!f) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/.test(f.type)) return setErr('Choose a picture (JPG, PNG, GIF or WebP).');
    if (f.size > 8 * 1024 * 1024) return setErr('The picture is larger than 8 MB.');
    setFiles((o) => ({ ...o, [slot]: f }));
  };

  const chosen = SLOTS.filter((s) => files[s.slot]);

  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      for (const s of chosen) await api.saveAd(date, s.slot, files[s.slot] as File);
      onChanged();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save the advertisement pictures.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slot: number) => {
    setBusy(true);
    setErr('');
    try {
      await api.deleteAd(date, slot);
      setCurrent((c) => (c || []).filter((a) => a.slot !== slot));
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not remove the picture.');
    } finally {
      setBusy(false);
    }
  };

  const removeAll = async () => {
    if (!window.confirm('Delete all advertisement pictures of this day?')) return;
    setBusy(true);
    setErr('');
    try {
      await api.deleteAllAds(date);
      setCurrent([]);
      setFiles({});
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not delete the pictures.');
    } finally {
      setBusy(false);
    }
  };

  const anyPicture = !!current?.length || SLOTS.some((s) => files[s.slot]);

  return (
    <Modal title="Advertisement pictures" onClose={onClose}>
      <p className="muted">
        Choose a picture for the <strong>left</strong>, the <strong>right</strong>, or both, for <strong>{longDate(date)}</strong>. A saved picture takes the place of
        that side's default video beside the logo, on every page and on every picture that is shared. A side with no picture keeps its default video.
        Pictures stay for <strong>one day only</strong>: they are deleted automatically when the date changes (at midnight).
      </p>
      {current === undefined && <p className="muted">Checking…</p>}
      <div className="pm-pair">
        {SLOTS.map((s) => {
          const cur = current?.find((a) => a.slot === s.slot);
          const src = previews[s.slot] || (cur ? adImageUrl(cur) : '');
          return (
            <div key={s.slot} className="pm-pick" data-slot={s.slot}>
              <strong>{s.label}</strong>
              <div className="pm-current">{src ? <img src={src} alt={s.label} /> : <span className="hint">No picture yet</span>}</div>
              <input
                ref={(el) => {
                  inputs.current[s.slot] = el;
                }}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                hidden
                onChange={(e) => {
                  pick(s.slot, e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              <button className="btn block" onClick={() => inputs.current[s.slot]?.click()} disabled={busy}>
                {src ? 'Choose another' : 'Choose picture'}
              </button>
              {cur && !files[s.slot] && (
                <button className="btn block danger" onClick={() => remove(s.slot)} disabled={busy}>
                  Delete picture
                </button>
              )}
            </div>
          );
        })}
      </div>
      {!anyPicture && <p className="hint">Choose at least one picture. A wide picture works well.</p>}
      {err && <p className="error">{err}</p>}
      <div className="actions">
        {anyPicture && (
          <button className="btn danger" onClick={removeAll} disabled={busy}>
            Delete all pictures
          </button>
        )}
        <button className="btn primary" onClick={save} disabled={!chosen.length || busy}>
          {busy ? 'Saving…' : 'Save and insert'}
        </button>
      </div>
    </Modal>
  );
}
